import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { colors } from '@/constants/colors';

/**
 * The shared look of the two pages a subscriber sees — apply, and their bill.
 *
 * ─── WHY THESE SHARE A SHELL ───
 * They are the operator's shopfront. To the person looking at them this is not
 * SmartFi, it is Aztech Fiber, and the two pages must feel like one business.
 * Ray, 2026-09-01, on the first drafts: *"laziness overload… you have to make
 * the bill portal modern, professional looking with operator logo, name,
 * address, contact."* Both pages were a bare form on white — indistinguishable
 * from a broken page, and nothing an operator would put their name to.
 *
 * ─── BUILT FOR A PHONE ───
 * Almost everyone opening these is on a phone, on mobile data, often because
 * their internet has just stopped. Big touch targets, one column, real
 * contrast, and nothing that needs a mouse.
 */

/** A tenant's public face. Every screen here takes the same shape. */
export interface PublicBrand {
   businessName: string;
   logoUrl: string;
   address: string;
   contactNumber: string;
}

export const PublicReset = createGlobalStyle`
   body { margin: 0; }
`;

/** Header carrying the operator's identity. The whole point of the redesign. */
export const BrandHeader: React.FC<{
   brand: PublicBrand;
   subtitle?: string;
}> = ({ brand, subtitle }) => (
   <Header>
      <HeaderInner>
         {brand.logoUrl ? (
            <Mark src={brand.logoUrl} alt="" />
         ) : (
            // Never an empty box: an operator who has not uploaded a logo still
            // gets something that looks deliberate.
            <MarkFallback>
               {brand.businessName.trim().charAt(0).toUpperCase() || '?'}
            </MarkFallback>
         )}
         <div>
            <BizName>{brand.businessName}</BizName>
            {subtitle ? <BizSub>{subtitle}</BizSub> : null}
         </div>
      </HeaderInner>
   </Header>
);

/**
 * The footer that answers "who do I call".
 *
 * On every page, because the person reading it usually has no internet and the
 * phone number is the only thing that still works for them.
 */
export const BrandFooter: React.FC<{ brand: PublicBrand }> = ({ brand }) => (
   <Footer>
      {brand.address ? <FootLine>{brand.address}</FootLine> : null}
      {brand.contactNumber ? (
         <FootLine>
            Call <CallLink href={`tel:${brand.contactNumber.replace(/\s/g, '')}`}>
               {brand.contactNumber}
            </CallLink>
         </FootLine>
      ) : null}
      <Powered>Powered by SmartFi</Powered>
   </Footer>
);

/**
 * The same gradient the bill page opens on.
 *
 * Ray, 2026-09-02, having asked more than once: *"so ugly, boring… it should
 * look professional, compact, modern, like a ewallet vibe."* The bill page was
 * rebuilt first; leaving sign-up and sign-in on a flat block made the product
 * feel like two different businesses stitched together. One surface, one look.
 */
const Header = styled.div`
   background: linear-gradient(160deg, #1e2a5a 0%, ${colors.PRIMARY} 100%);
   color: #fff;
   padding: 24px 20px 30px;
   border-radius: 0 0 26px 26px;
`;
const HeaderInner = styled.div`
   max-width: 520px;
   margin: 0 auto;
   display: flex;
   align-items: center;
   gap: 13px;
`;
const Mark = styled.img`
   width: 44px;
   height: 44px;
   border-radius: 13px;
   object-fit: cover;
   background: #fff;
   flex: none;
`;
const MarkFallback = styled.div`
   width: 44px;
   height: 44px;
   border-radius: 13px;
   background: rgba(255, 255, 255, 0.2);
   display: grid;
   place-items: center;
   font-size: 21px;
   font-weight: 800;
   flex: none;
`;
const BizName = styled.div`
   font-size: 18px;
   font-weight: 800;
   line-height: 1.25;
`;
const BizSub = styled.div`
   font-size: 13px;
   opacity: 0.85;
`;

export const Shell = styled.div`
   min-height: 100vh;
   background: ${colors.GRAY14};
   display: flex;
   flex-direction: column;
`;
export const Body = styled.div`
   flex: 1;
   width: 100%;
   max-width: 520px;
   margin: 0 auto;
   /* Pulled up over the header's curve, the way a wallet's first card sits. */
   padding: 0 16px 32px;
   margin-top: -16px;
   box-sizing: border-box;
`;
export const Card = styled.div`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 18px;
   padding: 20px;
   margin-bottom: 14px;
   box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
`;
export const CardTitle = styled.h2`
   margin: 0 0 4px;
   font-size: 15px;
   font-weight: 800;
`;
export const CardNote = styled.p`
   margin: 0 0 14px;
   font-size: 13px;
   line-height: 1.55;
   color: ${colors.GRAY5};
`;
export const Field = styled.div`
   margin-bottom: 14px;
`;
export const Lbl = styled.label`
   display: block;
   font-size: 13px;
   font-weight: 700;
   margin-bottom: 6px;
`;
export const Input = styled.input`
   width: 100%;
   box-sizing: border-box;
   font-family: inherit;
   font-size: 16px;
   padding: 14px 15px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 12px;
   background: ${colors.GRAY14};
   color: inherit;
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
      background: transparent;
   }
`;
export const Area = styled.textarea`
   width: 100%;
   box-sizing: border-box;
   font-family: inherit;
   font-size: 16px;
   padding: 13px 14px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 11px;
   background: transparent;
   color: inherit;
   resize: vertical;
`;
export const Select = styled.select`
   width: 100%;
   box-sizing: border-box;
   font-family: inherit;
   font-size: 16px;
   padding: 13px 14px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 11px;
   background: transparent;
   color: inherit;
`;
export const Hint = styled.div`
   margin-top: 5px;
   font-size: 12px;
   color: ${colors.GRAY5};
`;
export const Err = styled.p`
   margin: 0 0 12px;
   padding: 10px 12px;
   border-radius: 10px;
   font-size: 13px;
   font-weight: 600;
   color: ${colors.RED};
   background: rgba(220, 53, 69, 0.08);
`;
export const Primary = styled.button`
   width: 100%;
   font-family: inherit;
   font-size: 16px;
   font-weight: 800;
   padding: 16px;
   border: none;
   border-radius: 14px;
   background: ${colors.PRIMARY};
   color: #fff;
   cursor: pointer;
   box-shadow: 0 6px 16px rgba(55, 96, 249, 0.28);
   &:disabled {
      opacity: 0.5;
      box-shadow: none;
      cursor: default;
   }
`;
export const Fine = styled.p`
   margin: 10px 0 0;
   font-size: 12px;
   line-height: 1.55;
   color: ${colors.GRAY5};
   text-align: center;
`;
const Footer = styled.div`
   max-width: 520px;
   margin: 0 auto;
   padding: 4px 16px 28px;
   text-align: center;
`;
const FootLine = styled.div`
   font-size: 13px;
   color: ${colors.GRAY5};
   line-height: 1.7;
`;
const CallLink = styled.a`
   color: ${colors.PRIMARY};
   font-weight: 700;
   text-decoration: none;
`;
const Powered = styled.div`
   margin-top: 12px;
   font-size: 11px;
   color: ${colors.GRAY5};
   opacity: 0.75;
`;

/** One of the operator's payment methods, shown to a subscriber who owes. */
export const PayMethod: React.FC<{
   method: {
      id: string;
      kind: string;
      accountName: string;
      accountNumber: string;
      qrUrl: string;
      instructions: string;
   };
}> = ({ method }) => (
   <MethodBox>
      <MethodHead>
         <KindPill>{KIND_LABEL[method.kind] ?? method.kind}</KindPill>
         <MethodName>{method.accountName}</MethodName>
      </MethodHead>
      {method.accountNumber ? (
         <MethodNumber>{method.accountNumber}</MethodNumber>
      ) : null}
      {method.instructions ? (
         <MethodNote>{method.instructions}</MethodNote>
      ) : null}
      {/* Large on purpose: this gets scanned off a phone screen by another
          phone, and a thumbnail cannot be. */}
      {method.qrUrl ? <QrImg src={method.qrUrl} alt="Payment QR code" /> : null}
   </MethodBox>
);

const KIND_LABEL: Record<string, string> = {
   gcash: 'GCash',
   maya: 'Maya',
   bank: 'Bank transfer',
   cash: 'Cash',
   other: 'Other',
};

const MethodBox = styled.div`
   border: 1px solid ${colors.GRAY12};
   border-radius: 16px;
   padding: 16px;
   margin-bottom: 10px;
   text-align: center;
   background: ${colors.GRAY14};
`;
const MethodHead = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
   gap: 8px;
   flex-wrap: wrap;
`;
const KindPill = styled.span`
   font-size: 11px;
   font-weight: 800;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   padding: 3px 9px;
   border-radius: 999px;
   background: ${colors.GRAY14};
   color: ${colors.GRAY5};
`;
const MethodName = styled.span`
   font-size: 15px;
   font-weight: 700;
`;
const MethodNumber = styled.div`
   margin-top: 6px;
   font-size: 19px;
   font-weight: 800;
   letter-spacing: 0.5px;
`;
const MethodNote = styled.div`
   margin-top: 4px;
   font-size: 12px;
   color: ${colors.GRAY5};
`;
const QrImg = styled.img`
   margin-top: 12px;
   width: 100%;
   max-width: 220px;
   border-radius: 14px;
   background: #fff;
   padding: 8px;
   box-sizing: border-box;
`;
